import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Plus, Slack, FileCode, GitBranch, Trash2, Sparkles, Workflow } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import GoogleDriveSelector from './GoogleDriveSelector';
import SlackChannelSelector from './SlackChannelSelector';
import LinearTeamSelector from './LinearTeamSelector';
import GitHubRepoSelector from './GitHubRepoSelector';
import SourceTypeGrid from './SourceTypeGrid';
import SourceInstanceList from './SourceInstanceList';

export default function ProjectSourcesTab({ project, onUpdate }) {
  const [sources, setSources] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [channelSelectorOpen, setChannelSelectorOpen] = useState(false);
  const [teamSelectorOpen, setTeamSelectorOpen] = useState(false);
  const [driveSelectorOpen, setDriveSelectorOpen] = useState(false);
  const [repoSelectorOpen, setRepoSelectorOpen] = useState(false);
  const [selectedSlackSource, setSelectedSlackSource] = useState(null);
  const [selectedLinearSource, setSelectedLinearSource] = useState(null);
  const [selectedDriveSource, setSelectedDriveSource] = useState(null);
  const [selectedGitHubSource, setSelectedGitHubSource] = useState(null);
  const [expandedTypeId, setExpandedTypeId] = useState(null);
  const [newSource, setNewSource] = useState({
    source_conn_id: '',
    filter_type: 'channel',
    filter_value: '',
    filter_label: ''
  });

  useEffect(() => {
    loadData();
  }, [project.id]);

  const loadData = async () => {
    try {
      const [sourcesRes, availableRes] = await Promise.all([
        api.get(`/projects/${project.id}/sources`),
        api.get('/sources')
      ]);
      setSources(sourcesRes.data);
      setAvailableSources(availableRes.data || []);
    } catch (error) {
      toast.error('Failed to load sources');
    } finally {
      setLoading(false);
    }
  };

  // Group available (org-level) sources by type for the grid view
  const sourcesByType = availableSources.reduce((acc, source) => {
    const type = source.source_type?.toLowerCase() || '';
    if (!acc[type]) acc[type] = [];
    acc[type].push(source);
    return acc;
  }, {});

  const handleAddSource = async (e) => {
    e.preventDefault();

    if (newSource.filter_value) {
      const existingChannel = sources.find(
        (s) => s.filter_value === newSource.filter_value && s.source_conn_id === newSource.source_conn_id
      );
      if (existingChannel) {
        toast.error('This source is already added to the project');
        return;
      }
    }

    try {
      await api.post(`/projects/${project.id}/sources`, newSource);
      setAddDialogOpen(false);
      setNewSource({ source_conn_id: '', filter_type: 'channel', filter_value: '', filter_label: '' });
      setSelectedSlackSource(null);
      setSelectedLinearSource(null);
      setSelectedDriveSource(null);
      setSelectedGitHubSource(null);
      loadData();
      toast.success('Source added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add source');
    }
  };

  const handleRemoveSource = async (sourceId) => {
    try {
      await api.delete(`/projects/${project.id}/sources/${sourceId}`);
      setSources(sources.filter(s => s.id !== sourceId));
      toast.success('Source removed');
    } catch (error) {
      toast.error('Failed to remove source');
    }
  };

  const getSourceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'slack': return Slack;
      case 'linear': return Workflow;
      case 'jira': return FileCode;
      case 'github': return GitBranch;
      case 'google-drive': return FileCode;
      default: return FileCode;
    }
  };

  const handleGenerateMockEvents = async () => {
    try {
      const response = await api.post(`/projects/${project.id}/generate-mock-events`);
      toast.success(`Generated ${response.data.count} mock events!`);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate events');
    }
  };

  const handleInstanceDeleted = (instanceId) => {
    setAvailableSources(prev => prev.filter(s => s.id !== instanceId));
    // Also remove any project sources that referenced this instance
    setSources(prev => prev.filter(s => s.source_conn_id !== instanceId));
  };

  const handleInstanceRenamed = (instanceId, newName) => {
    setAvailableSources(prev =>
      prev.map(s => s.id === instanceId ? { ...s, display_name: newName } : s)
    );
  };

  // Group available sources for the dropdown with display names
  const groupedSources = availableSources.reduce((groups, source) => {
    const type = source.source_type?.toLowerCase() || 'other';
    if (!groups[type]) groups[type] = [];
    groups[type].push(source);
    return groups;
  }, {});

  const sourceTypeLabels = {
    slack: 'Slack',
    linear: 'Linear',
    jira: 'Jira',
    github: 'GitHub',
    'google-drive': 'Google Drive',
  };

  return (
    <div className="space-y-6" data-testid="project-sources-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-1">Connected Sources</h2>
          <p className="text-sm text-muted-foreground">Manage which tools feed into this project</p>
        </div>

        <div className="flex gap-2">
          {sources.length > 0 && (
            <Button
              variant="outline"
              onClick={handleGenerateMockEvents}
              data-testid="generate-mock-events-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Mock Events
            </Button>
          )}
          <Dialog
            open={addDialogOpen}
            onOpenChange={(open) => {
              setAddDialogOpen(open);
              if (!open) {
                setNewSource({ source_conn_id: '', filter_type: 'channel', filter_value: '', filter_label: '' });
                setSelectedSlackSource(null);
                setSelectedLinearSource(null);
                setSelectedDriveSource(null);
                setSelectedGitHubSource(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="add-source-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Source
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleAddSource}>
                <DialogHeader>
                  <DialogTitle>Add Source</DialogTitle>
                  <DialogDescription>
                    Connect a channel, repo, or project to this workspace
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <select
                      className="w-full p-2 border border-border rounded-md bg-background"
                      value={newSource.source_conn_id}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedSource = availableSources.find(s => s.id === selectedId);
                        const sourceType = selectedSource?.source_type?.toLowerCase();

                        const isSlack = sourceType === 'slack';
                        const isLinear = sourceType === 'linear';
                        const isDrive = sourceType === 'google-drive';
                        const isGitHub = sourceType === 'github';

                        let filterType = 'channel';
                        if (isLinear) filterType = 'team';
                        else if (isDrive) filterType = 'folder';
                        else if (isGitHub) filterType = 'repo';

                        setNewSource({
                          source_conn_id: selectedId,
                          filter_type: filterType,
                          filter_value: '',
                          filter_label: '',
                        });

                        setSelectedSlackSource(isSlack ? selectedId : null);
                        setSelectedLinearSource(isLinear ? selectedId : null);
                        setSelectedDriveSource(isDrive ? selectedId : null);
                        setSelectedGitHubSource(isGitHub ? selectedId : null);
                      }}
                      required
                    >
                      <option value="">Select a source...</option>
                      {Object.entries(groupedSources).map(([type, typeSources]) => (
                        <optgroup key={type} label={sourceTypeLabels[type] || type}>
                          {typeSources.map((source) => (
                            <option key={source.id} value={source.id}>
                              {source.display_name || source.source_type} — {source.connection_id}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {availableSources.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No sources available. Connect an integration first in Settings.
                      </p>
                    )}
                  </div>

                  {selectedSlackSource && (
                    <div className="space-y-2">
                      <Label>Channel</Label>
                      <Button type="button" variant="outline" className="w-full" onClick={() => setChannelSelectorOpen(true)}>
                        <Slack className="h-4 w-4 mr-2" />
                        {newSource.filter_label ? `Selected: ${newSource.filter_label}` : 'Select Slack Channel'}
                      </Button>
                    </div>
                  )}

                  {selectedLinearSource && (
                    <div className="space-y-2">
                      <Label>Team</Label>
                      <Button type="button" variant="outline" className="w-full" onClick={() => setTeamSelectorOpen(true)}>
                        <Workflow className="h-4 w-4 mr-2" />
                        {newSource.filter_label ? `Selected: ${newSource.filter_label}` : 'Select Linear Team'}
                      </Button>
                    </div>
                  )}

                  {selectedDriveSource && (
                    <div className="space-y-2">
                      <Label>File / Folder</Label>
                      <Button type="button" variant="outline" className="w-full" onClick={() => setDriveSelectorOpen(true)}>
                        <FileCode className="h-4 w-4 mr-2" />
                        {newSource.filter_label ? `Selected: ${newSource.filter_label}` : 'Select File or Folder'}
                      </Button>
                    </div>
                  )}

                  {selectedGitHubSource && (
                    <div className="space-y-2">
                      <Label>Repository</Label>
                      <Button type="button" variant="outline" className="w-full" onClick={() => setRepoSelectorOpen(true)}>
                        <GitBranch className="h-4 w-4 mr-2" />
                        {newSource.filter_label ? `Selected: ${newSource.filter_label}` : 'Select GitHub Repository'}
                      </Button>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Filter Label</Label>
                    <Input
                      placeholder="e.g., #general, website-proj"
                      value={newSource.filter_label}
                      onChange={(e) => setNewSource({ ...newSource, filter_label: e.target.value })}
                      required
                      disabled={(selectedSlackSource || selectedLinearSource || selectedDriveSource || selectedGitHubSource) && !!newSource.filter_label}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Filter Value</Label>
                    <Input
                      placeholder="Channel ID, project key, or repo name"
                      value={newSource.filter_value}
                      onChange={(e) => setNewSource({ ...newSource, filter_value: e.target.value })}
                      required
                      disabled={(selectedSlackSource || selectedLinearSource || selectedDriveSource || selectedGitHubSource) && !!newSource.filter_value}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">Add Source</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <SlackChannelSelector
            sourceConnId={selectedSlackSource}
            open={channelSelectorOpen}
            onOpenChange={setChannelSelectorOpen}
            onSelect={(channelData) => {
              setNewSource({ ...newSource, filter_type: 'channel', filter_value: channelData.filter_value, filter_label: channelData.filter_label });
            }}
          />

          <LinearTeamSelector
            sourceConnId={selectedLinearSource}
            open={teamSelectorOpen}
            onOpenChange={setTeamSelectorOpen}
            onSelect={(teamData) => {
              setNewSource({ ...newSource, filter_type: 'team', filter_value: teamData.filter_value, filter_label: teamData.filter_label });
            }}
          />

          <GoogleDriveSelector
            sourceConnId={selectedDriveSource}
            open={driveSelectorOpen}
            onOpenChange={setDriveSelectorOpen}
            onSelect={async (items) => {
              try {
                const promises = items.map(item =>
                  api.post(`/projects/${project.id}/sources`, {
                    source_conn_id: selectedDriveSource,
                    filter_type: item.type,
                    filter_value: item.filter_value,
                    filter_label: item.filter_label
                  })
                );
                await Promise.all(promises);
                toast.success(`Added ${items.length} source(s)`);
                setDriveSelectorOpen(false);
                setAddDialogOpen(false);
                loadData();
                setNewSource({ source_conn_id: '', filter_type: 'channel', filter_value: '', filter_label: '' });
                setSelectedDriveSource(null);
              } catch (error) {
                toast.error('Failed to add some sources');
              }
            }}
          />

          <GitHubRepoSelector
            sourceConnId={selectedGitHubSource}
            open={repoSelectorOpen}
            onOpenChange={setRepoSelectorOpen}
            onSelect={(repoData) => {
              setNewSource({ ...newSource, filter_type: 'repo', filter_value: repoData.filter_value, filter_label: repoData.filter_label });
            }}
          />
        </div>
      </div>

      {/* Two-level Grid/List view of org-level source connections */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Source Connections</h3>
        {expandedTypeId ? (
          <SourceInstanceList
            sourceType={expandedTypeId}
            instances={sourcesByType[expandedTypeId] || []}
            onBack={() => setExpandedTypeId(null)}
            onAdd={() => {
              // Redirect to integrations page for OAuth
              window.location.href = '/settings/integrations';
            }}
            onDelete={handleInstanceDeleted}
            onRename={handleInstanceRenamed}
          />
        ) : (
          <SourceTypeGrid
            sourcesByType={sourcesByType}
            onSelectType={setExpandedTypeId}
          />
        )}
      </div>

      {/* Project-level sources (resource filters) */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Project Sources</h3>
        {sources.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-4">No sources connected to this project yet</p>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add your first source
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {sources.map((source) => {
              const Icon = getSourceIcon(source.source_type);
              // Find the parent connection to show its display_name
              const parentConn = availableSources.find(s => s.id === source.source_conn_id);
              return (
                <Card key={source.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{source.filter_label}</span>
                          <Badge variant="secondary">{source.source_type}</Badge>
                          {parentConn?.display_name && (
                            <span className="text-xs text-muted-foreground">
                              via {parentConn.display_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{source.filter_value}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveSource(source.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
